import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

export interface MenuItem {
  id: string;
  label: string;
  icon: any;
}

export interface MenuGroup {
  id: string;
  label: string;
  description: string;
  items: MenuItem[];
}

interface ComptabilitatMenuProps {
  groups: MenuGroup[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export function ComptabilitatMenu({ groups, activeTab, onTabChange }: ComptabilitatMenuProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Load expanded groups from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('comptabilitat-menu-expanded');
    if (saved) {
      try {
        setExpandedGroups(new Set(JSON.parse(saved)));
      } catch {
        // Si hay error al parsear, usar el grupo por defecto (Operativo)
        setExpandedGroups(new Set(['Operativa']));
      }
    } else {
      // Por defecto, expandir el primer grupo (Operativa)
      setExpandedGroups(new Set(['Operativa']));
    }
  }, []);

  // Guardar grupos expandidos en localStorage cuando cambien
  useEffect(() => {
    localStorage.setItem('comptabilitat-menu-expanded', JSON.stringify(Array.from(expandedGroups)));
  }, [expandedGroups]);

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.id);

        return (
          <div
            key={group.id}
            className="border border-border rounded-lg overflow-hidden bg-card hover:bg-card/80 transition-colors"
          >
            {/* Group Header - Clickable */}
            <button
              onClick={() => toggleGroup(group.id)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors"
            >
              <div className="flex-1 text-left">
                <h3 className="font-semibold text-sm text-foreground">{group.label}</h3>
                <p className="text-xs text-muted-foreground mt-1">{group.description}</p>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ml-2 flex-shrink-0 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            </button>

            {/* Group Items - Expandable */}
            {isExpanded && (
              <div className="border-t border-border bg-muted/30 p-3 space-y-2">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;

                  return (
                    <button
                      key={item.id}
                      onClick={() => onTabChange(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-sm ${
                        isActive
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
